import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Assignment as VhcIcon,
} from '@mui/icons-material';
import bookingService from '../services/bookingService';
import api from '../services/api';

const BookingEditModal = ({ open, onClose, booking, onSuccess }) => {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = ['admin', 'org_admin', 'garage_manager'].includes(user?.role) || user?.is_superuser;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [vhcStatus, setVhcStatus] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [assignedTechnicianId, setAssignedTechnicianId] = useState('');
  const [formData, setFormData] = useState({
    // Customer details
    customerName: '',
    customerEmail: '',
    customerPhone: '',

    // Vehicle details
    vehicleRegistration: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleFuel: '',
    vehicleTransmission: '',
    vehicleEngineSize: '',

    // Booking details
    bookingDate: '',
    bookingTime: '',
    status: 'confirmed',

    // Services
    services: [{ service: '', cost: '' }],

    // Notes
    notes: '',
  });

  // Fetch technicians list
  const fetchTechnicians = async () => {
    try {
      const response = await api.get('/v1/technicians/');
      setTechnicians(response.data || []);
    } catch (err) {
      console.error('Failed to fetch technicians:', err);
    }
  };

  // Populate form with booking data when modal opens
  useEffect(() => {
    if (booking && open) {
      const services = booking.work_required && booking.work_required.length > 0
        ? booking.work_required.map(w => ({
            service: w.service || '',
            cost: w.cost !== undefined ? String(w.cost) : '',
          }))
        : [{ service: '', cost: '' }];

      setFormData({
        customerName: booking.customer?.name || '',
        customerEmail: booking.customer?.email || '',
        customerPhone: booking.customer?.phone || '',
        vehicleRegistration: booking.vehicle?.registration || '',
        vehicleMake: booking.vehicle?.make || '',
        vehicleModel: booking.vehicle?.model || '',
        vehicleFuel: booking.vehicle?.fuel_type || '',
        vehicleTransmission: booking.vehicle?.transmission || '',
        vehicleEngineSize: booking.vehicle?.engine_size || '',
        bookingDate: booking.booking_date || '',
        bookingTime: booking.time_of_day || '',
        status: booking.status || 'confirmed',
        services: services,
        notes: booking.notes || '',
      });
      setAssignedTechnicianId(booking.assigned_technician?.id || '');
      setVhcStatus(booking.health_check?.status || '');
      setError(null);

      // Fetch technicians list
      fetchTechnicians();
    }
  }, [booking, open]);

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleDateChange = (event) => {
    setFormData({
      ...formData,
      bookingDate: event.target.value,
    });
  };

  const handleServiceChange = (index, field) => (event) => {
    const newServices = [...formData.services];
    newServices[index][field] = event.target.value;
    setFormData({
      ...formData,
      services: newServices,
    });
  };

  const handleAddService = () => {
    setFormData({
      ...formData,
      services: [...formData.services, { service: '', cost: '' }],
    });
  };

  const handleRemoveService = (index) => {
    const newServices = formData.services.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      services: newServices.length > 0 ? newServices : [{ service: '', cost: '' }],
    });
  };

  const calculateTotalCost = () => {
    return formData.services.reduce((total, service) => {
      const cost = parseFloat(service.cost) || 0;
      return total + cost;
    }, 0);
  };

  const validateForm = () => {
    if (!formData.customerName.trim()) {
      setError('Customer name is required');
      return false;
    }
    if (!formData.vehicleRegistration.trim()) {
      setError('Vehicle registration is required');
      return false;
    }
    if (!formData.bookingDate) {
      setError('Booking date is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Format services
      const workRequired = formData.services
        .filter(s => s.service.trim())
        .map(s => ({
          service: s.service.trim(),
          cost: parseFloat(s.cost) || 0,
        }));

      // Prepare update data
      const updateData = {
        customer: {
          name: formData.customerName.trim(),
          email: formData.customerEmail.trim(),
          phone: formData.customerPhone.trim(),
        },
        vehicle: {
          registration: formData.vehicleRegistration.trim().toUpperCase(),
          make: formData.vehicleMake.trim(),
          model: formData.vehicleModel.trim(),
          fuel_type: formData.vehicleFuel,
          transmission: formData.vehicleTransmission,
          engine_size: formData.vehicleEngineSize.trim(),
        },
        booking_date: formData.bookingDate,
        time_of_day: formData.bookingTime,
        status: formData.status,
        assigned_technician_id: assignedTechnicianId || null,
        work_required: workRequired,
        notes: formData.notes.trim(),
      };

      const response = await bookingService.updateBooking(booking.id, updateData);

      // Update VHC status if changed (admin only)
      if (isAdmin && booking.health_check && vhcStatus !== booking.health_check.status) {
        await api.patch(`/v1/evhc/${booking.health_check.id}/`, { status: vhcStatus });
      }

      // Success
      if (onSuccess) {
        onSuccess(response);
      }

      onClose();
    } catch (err) {
      console.error('Error updating booking:', err);
      setError(err.response?.data?.error || 'Failed to update booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const handleSaveClick = () => {
    if (!validateForm()) {
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmSave = () => {
    setConfirmDialogOpen(false);
    handleSubmit();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Booking</Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Customer Information */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Customer Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Customer Name"
              value={formData.customerName}
              onChange={handleInputChange('customerName')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.customerEmail}
              onChange={handleInputChange('customerEmail')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.customerPhone}
              onChange={handleInputChange('customerPhone')}
              disabled={loading}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Vehicle Information */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Vehicle Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Registration Number"
              value={formData.vehicleRegistration}
              onChange={handleInputChange('vehicleRegistration')}
              disabled={loading}
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Make"
              value={formData.vehicleMake}
              onChange={handleInputChange('vehicleMake')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Model"
              value={formData.vehicleModel}
              onChange={handleInputChange('vehicleModel')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Fuel Type</InputLabel>
              <Select
                value={formData.vehicleFuel}
                label="Fuel Type"
                onChange={handleInputChange('vehicleFuel')}
                disabled={loading}
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="Petrol">Petrol</MenuItem>
                <MenuItem value="Diesel">Diesel</MenuItem>
                <MenuItem value="Electric">Electric</MenuItem>
                <MenuItem value="Hybrid">Hybrid</MenuItem>
                <MenuItem value="LPG">LPG</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Transmission</InputLabel>
              <Select
                value={formData.vehicleTransmission}
                label="Transmission"
                onChange={handleInputChange('vehicleTransmission')}
                disabled={loading}
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="Automatic">Automatic</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Engine Size"
              value={formData.vehicleEngineSize}
              onChange={handleInputChange('vehicleEngineSize')}
              disabled={loading}
              placeholder="e.g., 1.6L or 1248cc"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Booking Information */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Booking Details
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Booking Date"
              type="date"
              value={formData.bookingDate}
              onChange={handleDateChange}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Time of Day</InputLabel>
              <Select
                value={formData.bookingTime}
                label="Time of Day"
                onChange={handleInputChange('bookingTime')}
                disabled={loading}
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="morning">Morning</MenuItem>
                <MenuItem value="afternoon">Afternoon</MenuItem>
                <MenuItem value="evening">Evening</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={handleInputChange('status')}
                disabled={loading}
              >
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="waiting_parts">Waiting for Parts</MenuItem>
                <MenuItem value="pending_admin_review">Pending Review</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Assigned Technician</InputLabel>
              <Select
                value={assignedTechnicianId}
                label="Assigned Technician"
                onChange={(e) => setAssignedTechnicianId(e.target.value)}
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {technicians.map((tech) => (
                  <MenuItem key={tech.id} value={tech.id}>
                    {tech.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Services */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            Services Required
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddService}
            disabled={loading}
          >
            Add Service
          </Button>
        </Box>

        {formData.services.map((service, index) => (
          <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label="Service Description"
                value={service.service}
                onChange={handleServiceChange(index, 'service')}
                disabled={loading}
                placeholder="e.g., Oil Change, MOT Test"
              />
            </Grid>
            <Grid item xs={10} sm={4}>
              <TextField
                fullWidth
                label="Cost"
                type="number"
                value={service.cost}
                onChange={handleServiceChange(index, 'cost')}
                disabled={loading}
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={2} sm={1}>
              <IconButton
                onClick={() => handleRemoveService(index)}
                disabled={loading || formData.services.length === 1}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="h6" align="right">
            Total Cost: £{calculateTotalCost().toFixed(2)}
          </Typography>
        </Box>

        {/* VHC Section (Admin only, only if VHC exists) */}
        {isAdmin && booking?.health_check && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <VhcIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Vehicle Health Check
                </Typography>
              </Box>

              {/* Status counts: Red/Amber/Green */}
              <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
                <Chip
                  label={`${booking.health_check.red_count} Red`}
                  size="small"
                  sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}
                />
                <Chip
                  label={`${booking.health_check.amber_count} Amber`}
                  size="small"
                  sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}
                />
                <Chip
                  label={`${booking.health_check.green_count} Green`}
                  size="small"
                  sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}
                />
              </Box>

              {/* Status dropdown */}
              <FormControl fullWidth size="small">
                <InputLabel>VHC Status</InputLabel>
                <Select
                  value={vhcStatus}
                  label="VHC Status"
                  onChange={(e) => setVhcStatus(e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="reviewed">Reviewed</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Notes */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Additional Notes
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Notes"
          value={formData.notes}
          onChange={handleInputChange('notes')}
          disabled={loading}
          placeholder="Any additional information about the booking..."
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveClick}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent>
          <Typography>
            All changes will be recorded for audit purposes. Do you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmSave} variant="contained" color="primary">
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default BookingEditModal;
